/**
 * Der Pruefschritt als reine Abbildung: ausgelesenes `invoice` ↔ Formularwerte (Feldtext) ↔ bearbeitetes `invoice`.
 *
 * Werte sind Zeichenketten (kanonische Form, `formular-echte-aenderung.md` Regel 1–3): leer ist `''`, ein Datum ist
 * `YYYY-MM-DD` wie es aus der Route kommt — kein `Date` im Formular.
 */
import type { ExtractResponse, FieldSource, InvoiceDraft } from '@/api/pdfExtractClient'
import { type ReviewAttachment, toInvoiceAttachments } from './attachments'
import type { ManualTotals } from './manual-items'

export interface ReviewValues {
  invoiceNumber: string
  issueDate: string
  dueDate: string
  buyerName: string
  buyerStreet: string
  buyerPostalCode: string
  buyerCity: string
  buyerEmail: string
  /** BT-10 — fuer deutsche Empfaenger Pflicht (Leitweg-ID bei oeffentlichen Auftraggebern). */
  buyerReference: string
}

export type ReviewField = keyof ReviewValues

/**
 * Obergrenzen der Pruefschritt-Felder (`textfeld-obergrenzen.md`, Hausprazedenz): Belegnummer 100 wie
 * `invoice.orderNumber`, Name/Strasse 200, PLZ 20, Ort 100, E-Mail 254, BT-10 50 wie eine Partei-Kennung
 * (`partyIdentifierSchema`, eine Leitweg-ID hat bis 46 Zeichen).
 */
export const REVIEW_LIMITS: Partial<Record<ReviewField, number>> = {
  invoiceNumber: 100,
  buyerName: 200,
  buyerStreet: 200,
  buyerPostalCode: 20,
  buyerCity: 100,
  buyerEmail: 254,
  buyerReference: 50,
}

const str = (v: string | null | undefined): string => (typeof v === 'string' ? v : '')

export function toReviewValues(invoice: InvoiceDraft): ReviewValues {
  return {
    invoiceNumber: str(invoice.invoiceNumber),
    issueDate: str(invoice.issueDate),
    dueDate: str(invoice.dueDate),
    buyerName: str(invoice.buyer?.name),
    buyerStreet: str(invoice.buyer?.street),
    buyerPostalCode: str(invoice.buyer?.postalCode),
    buyerCity: str(invoice.buyer?.city),
    buyerEmail: str(invoice.buyer?.email),
    buyerReference: str(invoice.countrySpecific?.buyerReference),
  }
}

/**
 * Das ausgelesene `invoice` mit den Eingaben des Pruefschritts — alles Uebrige bleibt, wie es kam. Anlagen (E18)
 * kommen hinter die, die das `invoice` schon fuehrt.
 */
export function applyReview(
  invoice: InvoiceDraft,
  values: ReviewValues,
  attachments: readonly ReviewAttachment[] = [],
  manual: ManualTotals | null = null
): InvoiceDraft {
  const trimmed = (v: string) => v.trim()
  const existing = Array.isArray(invoice.attachments) ? invoice.attachments : []
  return {
    ...invoice,
    ...(manual
      ? {
          items: manual.items,
          taxSummary: manual.taxSummary,
          subtotal: manual.subtotal,
          total: manual.total,
        }
      : {}),
    ...(existing.length > 0 || attachments.length > 0
      ? { attachments: [...existing, ...toInvoiceAttachments(attachments)] }
      : {}),
    invoiceNumber: trimmed(values.invoiceNumber),
    issueDate: trimmed(values.issueDate),
    dueDate: trimmed(values.dueDate),
    buyer: {
      ...(invoice.buyer ?? {}),
      name: trimmed(values.buyerName),
      street: trimmed(values.buyerStreet),
      postalCode: trimmed(values.buyerPostalCode),
      city: trimmed(values.buyerCity),
      countryCode: invoice.buyer?.countryCode || 'DE',
      email: trimmed(values.buyerEmail),
    },
    countrySpecific: {
      ...(invoice.countrySpecific ?? {}),
      buyerReference: trimmed(values.buyerReference),
    },
  }
}

/**
 * Herkunft je Formularfeld: der Schluessel in `fields`. Das Backend schluesselt nach **Rechnungspfad** (Plan-Tabelle
 * „Feldabbildung", `src/pdf-extract/feldabbildung.ts`: `buyer.name`, `countrySpecific.buyerReference` …).
 * 🔴 Gemessen 2026-11-01 im Live-Beleg: Mit den Preset-Namen (`buyerName`, `buyerAddress`) fand die Seite fuer den
 * Kaeufer keine Herkunft, die ausgelesenen Kaeuferfelder standen ohne „erkannt".
 */
const FIELD_KEYS: Record<ReviewField, readonly string[]> = {
  invoiceNumber: ['invoiceNumber'],
  issueDate: ['issueDate'],
  dueDate: ['dueDate'],
  buyerName: ['buyer.name'],
  buyerStreet: ['buyer.street'],
  buyerPostalCode: ['buyer.postalCode'],
  buyerCity: ['buyer.city'],
  buyerEmail: ['buyer.email'],
  buyerReference: ['countrySpecific.buyerReference'],
}

/** Steht nie auf einem PDF (Template 06: „neither the buyer's e-mail address nor the Leitweg-ID is printed"). */
const NOT_ON_PDF: ReadonlySet<ReviewField> = new Set(['buyerEmail', 'buyerReference'])

export function sourceOf(fields: ExtractResponse['fields'], keys: readonly string[]): FieldSource {
  for (const key of keys) {
    const entry = fields[key]
    if (entry) return entry.source
  }
  return null
}

/**
 * Ohne diese Felder geht es nicht weiter (E19/E22): Nummer und Datum stehen auf jeder Rechnung. Die Fälligkeit fordert BR-CO-25, sobald
 * ein Betrag zu zahlen ist; die Seite setzt keine Zahlungsbedingung, ohne Datum lehnt die Prüfung ab (gemessen 11-04). Eine Gutschrift
 * hat nichts zu zahlen.
 */
export function requiredReviewFields(invoice: InvoiceDraft): ReviewField[] {
  return invoice.type === 'credit_note'
    ? ['invoiceNumber', 'issueDate']
    : ['invoiceNumber', 'issueDate', 'dueDate']
}

/**
 * E22: Was das Auslesen nicht gefunden hat, fragt der Prüfschritt gesammelt ab. Maßgeblich ist der Stand nach dem Auslesen, nicht die
 * Eingabe: Ein Feld springt beim Tippen nicht aus dem Bereich. Pflichtfelder zuerst.
 */
export function askedReviewFields(
  extracted: ReviewValues,
  required: readonly ReviewField[]
): ReviewField[] {
  const empty = (Object.keys(extracted) as ReviewField[]).filter(
    (field) => extracted[field].trim() === ''
  )
  return [
    ...empty.filter((field) => required.includes(field)),
    ...empty.filter((field) => !required.includes(field)),
  ]
}

/** Das Etikett neben einem Feld (E16: Herkunft statt Markierung im PDF). */
export type FieldTag = 'recognized' | 'profile' | 'required' | 'missing' | 'add' | 'changed' | null

export function tagFor(
  field: ReviewField,
  value: string,
  extracted: ReviewValues,
  fields: ExtractResponse['fields'],
  required: readonly ReviewField[] = []
): FieldTag {
  if (value.trim() === '') {
    if (required.includes(field)) return 'required'
    return NOT_ON_PDF.has(field) ? 'add' : 'missing'
  }
  if (value !== extracted[field]) return 'changed'
  const source = sourceOf(fields, FIELD_KEYS[field])
  if (source === 'pdf') return 'recognized'
  if (source === 'profile') return 'profile'
  return null
}

/** Stimmen Positionen, Netto, Steuer und Brutto ueberein? Nein, sobald das Auslesen einen Summen-Fehler meldet. */
export function totalsConsistent(checks: ExtractResponse['checks']): boolean {
  return !checks.some(
    (c) =>
      c.severity === 'error' &&
      (c.code.includes('TOTALS_MISMATCH') || c.code.includes('TAX_MISMATCH'))
  )
}
